<template>
  <section class="plans-page">
    <div class="plans-page__container">
      <!-- Header -->
      <div class="plans-page__hero">
        <p class="plans-page__label">PLANES</p>
        <h1 class="plans-page__title">
          Elige la estructura que necesita tu negocio
        </h1>
        <p class="plans-page__subtitle">
          Desde cumplir con el SRI y tomar decisiones con inteligencia real.
        </p>
        <BillingToggle class="mt-4" />
      </div>

      <!-- Loading skeletons -->
      <div v-if="loading" class="plans-page__grid">
        <PlanCardSkeleton
          v-for="n in 3"
          :key="'sk-' + n"
          :featureCount="6 + n"
        />
      </div>

      <!-- Plan cards grid -->
      <div v-else-if="activePlans.length" class="plans-page__carousel-wrap">
        <div
          ref="plansGrid"
          class="plans-page__grid"
          @scroll.passive="onGridScroll"
        >
          <NewPlanCard
            v-for="plan in activePlans"
            :key="plan.slug"
            :plan="plan"
            :marketing-addon-plan="marketingAddonPlan"
            :marketing-addon-active="marketingAddonActive"
            @select="handlePlanSelected"
          />
        </div>
        <div class="plans-page__dots">
          <span
            v-for="(plan, i) in activePlans"
            :key="plan.slug"
            class="plans-page__dot"
            :class="{ 'plans-page__dot--active': activeCard === i }"
            @click="scrollToCard(i)"
          />
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="plans-page__empty">
        <p>No hay planes disponibles en este momento.</p>
        <p>Por favor intenta m&aacute;s tarde o contacta a soporte.</p>
      </div>

      <!-- Módulo de marketing (WhatsApp) — también comprable desde el checkbox
           de cada plan; este banner es para clientes que ya tienen plan y
           solo quieren agregar el módulo sin volver a comprar el plan. -->
      <router-link
        v-if="!marketingAddonActive"
        :to="{ name: 'plans.additionals' }"
        class="plans-page__firma-section"
      >
        <div class="plans-page__firma-info">
          <div class="plans-page__firma-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
              />
            </svg>
          </div>
          <div>
            <p class="plans-page__firma-title">
              Módulo de marketing (WhatsApp)
            </p>
            <p class="plans-page__firma-desc">
              Conversaciones, bots y automatizaciones de WhatsApp para tu
              negocio. Actívalo sin cambiar tu plan actual.
            </p>
          </div>
        </div>
        <div class="plans-page__firma-price-card" v-if="marketingAddonPlan">
          <p class="plans-page__firma-price-label">Desde</p>
          <p class="plans-page__firma-price-amount">
            ${{ marketingAddonPlan.standalone_price_monthly }}
          </p>
          <p class="plans-page__firma-price-iva">/ mes + IVA</p>
        </div>
      </router-link>

      <!-- Firma electronica section -->
      <router-link
        :to="{ name: 'plans.additionals' }"
        class="plans-page__firma-section"
      >
        <div class="plans-page__firma-info">
          <div class="plans-page__firma-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
            </svg>
          </div>
          <div>
            <p class="plans-page__firma-title">Firma electr&oacute;nica</p>
            <p class="plans-page__firma-desc">
              Servicio adicional. Firma documentos con validez legal en Ecuador.
              Act&iacute;valo cuando lo necesites.
            </p>
          </div>
        </div>
        <div class="plans-page__firma-price-card">
          <p class="plans-page__firma-price-label">Por firma</p>
          <p class="plans-page__firma-price-amount">$24</p>
          <p class="plans-page__firma-price-iva">+ IVA</p>
        </div>
      </router-link>

      <!-- Custom plan banner -->
      <!-- Licencias de usuario adicionales (multi-usuario) -->
      <ExtraSeatsCard />

      <CustomPlanBanner
        class="plans-page__custom-banner"
        @contact="handleContact"
      />

      <!-- FAQ -->
      <PlansFAQ class="plans-page__faq" />
    </div>

    <!-- Checkout dialog -->
    <el-dialog
      :visible.sync="dialogPlan"
      :fullscreen="true"
      class="plan-dialog plan-dialog--modern"
      :before-close="handleClose"
    >
      <template slot="title">
        <div class="plan-dialog__header">
          <LogoComponent color="blue" width="32" />
        </div>
      </template>

      <div class="plan-dialog__body">
        <div class="plan-dialog__layout">
          <div class="plan-dialog__plan-card">
            <NewPlanCard
              v-if="dialogPlan && selectedPlan"
              :plan="selectedPlan"
              :checkout="true"
            />
          </div>
          <div class="plan-dialog__payment">
            <PlanPayProcessComponent v-if="dialogPlan" :modal="true" />
          </div>
        </div>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import BillingToggle from "../../components/BillingToggle";
import NewPlanCard from "../../components/NewPlanCard";
import PlanCardSkeleton from "../../components/PlanCardSkeleton";
import CustomPlanBanner from "../../components/CustomPlanBanner";
import PlansFAQ from "../../components/PlansFAQ";
import ExtraSeatsCard from "../../components/ExtraSeatsCard";
import PlanPayProcessComponent from "../../components/PayphoneProccess";
import LogoComponent from "@/components/Logo/Logo";
import payService from "../../services/payService";

export default {
  name: "PlansMainPage",
  components: {
    ExtraSeatsCard,
    BillingToggle,
    NewPlanCard,
    PlanCardSkeleton,
    CustomPlanBanner,
    PlansFAQ,
    PlanPayProcessComponent,
    LogoComponent
  },
  data: () => ({
    dialogPlan: false,
    loading: true,
    activeCard: 0,
    marketingAddonPlan: null,
    marketingAddonActive: false
  }),
  computed: {
    activePlans() {
      return this.$store.getters.activePlans;
    },
    selectedPlan() {
      return this.$store.getters.selectedPlan;
    }
  },
  async created() {
    this.loading = true;
    await Promise.all([
      this.$store.dispatch("fetchPlans"),
      this.$store.dispatch("fetchUserPlan")
    ]);
    this.loading = false;
    this.loadMarketingAddon();
  },
  methods: {
    async loadMarketingAddon() {
      // Best-effort: si falla, el checkbox del addon simplemente no aparece.
      try {
        const [{ data: planRes }, { data: statusRes }] = await Promise.all([
          payService.getWhatsappAddonPlan(),
          payService.getWhatsappAddonStatus()
        ]);
        if (planRes && planRes.success) this.marketingAddonPlan = planRes.data;
        if (statusRes && statusRes.success) {
          this.marketingAddonActive = !!(
            statusRes.data && statusRes.data.active
          );
        }
      } catch (e) {
        // silencioso a propósito
      }
    },
    handlePlanSelected() {
      this.dialogPlan = true;
    },
    handleClose() {
      this.dialogPlan = false;
    },
    handleContact() {
      window.open("https://wa.me/593967863298", "_blank");
    },
    onGridScroll() {
      const grid = this.$refs.plansGrid;
      if (!grid) return;
      const cardWidth = grid.firstElementChild
        ? grid.firstElementChild.offsetWidth + 12
        : 1;
      this.activeCard = Math.round(grid.scrollLeft / cardWidth);
    },
    scrollToCard(index) {
      const grid = this.$refs.plansGrid;
      if (!grid) return;
      const card = grid.children[index];
      if (!card) return;
      grid.scrollTo({ left: card.offsetLeft - 16, behavior: "smooth" });
    }
  }
};
</script>

<style lang="scss" scoped>
.plans-page {
  font-family: "Plus Jakarta Sans", system-ui, -apple-system, sans-serif;
  max-width: 1140px;
  margin: 0 auto;
  padding: 48px 20px;

  &__hero {
    text-align: center;
    margin-bottom: 32px;
  }

  &__label {
    font-size: 12px;
    letter-spacing: 0.1em;
    color: #3ecfb4;
    margin-bottom: 10px;
    font-weight: 600;
  }

  &__title {
    font-size: 30px;
    font-weight: 700;
    color: #2d2b55;
    margin: 0 0 12px;
    line-height: 1.25;
  }

  &__subtitle {
    font-size: 15px;
    color: #888780;
    margin: 0 auto 28px;
    max-width: 480px;
    line-height: 1.6;
  }

  &__coupon {
    margin-bottom: 24px;
  }

  &__carousel-wrap {
    margin-bottom: 36px;
  }

  &__grid {
    display: flex;
    gap: 14px;
    align-items: start;
    justify-content: center;
    overflow-x: auto;
    overflow-y: visible;
    padding-top: 16px;
    padding-bottom: 8px;
    -webkit-overflow-scrolling: touch;

    > * {
      min-width: 280px;
      flex: 0 0 280px;
    }

    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__dots {
    display: none;
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d0cecc;
    cursor: pointer;
    transition: background 0.2s, transform 0.2s;

    &--active {
      background: #3ecfb4;
      transform: scale(1.3);
    }
  }

  &__firma-section {
    margin-bottom: 36px;
    background: #fff;
    border: 1px solid #e8ecf1;
    border-radius: 14px;
    padding: 22px 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    cursor: pointer;
    text-decoration: none;
    transition: border-color 0.18s, box-shadow 0.18s;

    &:hover {
      border-color: #3ecfb4;
      box-shadow: 0 2px 12px rgba(62, 207, 180, 0.12);
      text-decoration: none;
    }
  }

  &__firma-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }

  &__firma-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #2d2b55;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__firma-title {
    font-size: 15px;
    font-weight: 700;
    color: #2d2b55;
    margin: 0 0 2px;
  }

  &__firma-desc {
    font-size: 13px;
    color: #888780;
    margin: 0;
  }

  &__firma-price-card {
    background: #fff;
    border: 1px solid #e8e7e3;
    border-radius: 10px;
    padding: 12px 24px;
    text-align: center;
    flex-shrink: 0;
  }

  &__firma-price-label {
    font-size: 12px;
    color: #888780;
    margin: 0 0 2px;
    font-weight: 500;
  }

  &__firma-price-amount {
    font-size: 22px;
    font-weight: 700;
    color: #2d2b55;
    margin: 0;
  }

  &__firma-price-iva {
    font-size: 11px;
    color: #b4b2a9;
    margin: 2px 0 0;
  }

  &__custom-banner {
    margin-bottom: 36px;
  }

  &__faq {
    margin-bottom: 28px;
  }

  &__empty {
    text-align: center;
    padding: 48px 20px;
    color: #888780;
    font-size: 14px;
    background: #f7f8fc;
    border-radius: 14px;
    margin-bottom: 36px;

    p {
      margin: 0 0 4px;
    }
  }

  &__footer {
    text-align: center;
    padding: 20px 0 8px;

    p {
      font-size: 13px;
      color: #b4b2a9;
      margin: 0;
    }
  }
}

// Responsive
@media (max-width: 900px) {
  .plans-page__firma-section {
    flex-direction: column;
    text-align: center;

    .plans-page__firma-info {
      flex-direction: column;
    }
  }
}

@media (max-width: 767px) {
  .plans-page__grid {
    justify-content: flex-start;
    scroll-snap-type: x mandatory;
    padding: 16px 16px 8px;
    gap: 12px;

    > * {
      min-width: 82vw;
      flex: 0 0 82vw;
      scroll-snap-align: start;
    }
  }

  .plans-page__dots {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 14px;
  }
}

@media (max-width: 640px) {
  .plans-page {
    padding: 24px 16px;
  }

  .plans-page__title {
    font-size: 24px;
  }
}
</style>
